/* eslint-disable sonarjs/cognitive-complexity */
import { jsx } from 'panel';
import { SectionEl } from 'common/components/section';
import { SelectEl, THEME } from 'common/components/select';
import { SvgIconEl } from 'common/components/svg-icon';
import { ToggleEl } from 'common/components/toggle';
import { TooltipEl } from 'common/components/tooltip';
import { DataGroupSelectorEl } from '../data-group-selector';
import { QueryEntrySectionEl } from '../query-entry-section';
import { EntryType, RowLabelType } from '../query-entry-section/types';
import { QueryReportHeaderEl } from '../query-report-header';
import { hasNewQBUI } from './util';
export function template() {
  const hasNewUI = hasNewQBUI();
  const entries = this.helpers.getEntries("entries");
  const globalSegments = this.helpers.getSegmentEntries();

  const FilterOrOperatorInfo = () => jsx("div", {
    sel: '.mp-query-filter-operator-info'
  }, "Matching Any", jsx("div", {
    sel: '.mp-query-filter-operator-icon'
  }, jsx(SvgIconEl, {
    attrs: {
      icon: "info"
    }
  }), jsx(TooltipEl, {
    attrs: {
      'max-width': "225px"
    }
  }, "The \"Any\" operator is deprecated and cannot be changed for this report")));

  const AttributionControl = props => this.helpers.showAttributionSelector() && jsx("div", {
    sel: '.breakdown-attribution-wrapper'
  }, !hasNewUI && jsx("div", {
    sel: '.text'
  }, "Attribution type"), jsx(SelectEl, {
    attrs: {
      elref: "attributionSelect",
      disabled: Boolean(props === null || props === void 0 ? void 0 : props.disabled),
      sections: JSON.stringify([{
        items: this.attr("breakdown-attribution")
      }]),
      'selected-option': this.helpers.getAttributionSelectedOption(),
      theme: hasNewUI ? THEME.TERTIARY : THEME.BUILDER,
      'menu-width': 200,
      'tab-index': 0
    },
    on: {
      change: this.helpers.updateAttributionSelection
    },
    props: {
      contextPanelTransform: this.helpers.getAttributionContextPanel
    }
  }));

  const GlobalBreakdowns = _ref => {
    let {
      segmentEntries
    } = _ref;
    return !this.attr("hide-group-by") && jsx(QueryEntrySectionEl, {
      key: "".concat(this.attr("selected-query-type"), "-breakdowns"),
      attrs: this.helpers.serializeAttributes(Object.assign(this.helpers.getPropertyMenuAttributes("breakdown"), {
        'can-customize-numeric-buckets': this.attr("can-customize-numeric-buckets"),
        'data-group-id': this.attr("data-group-id"),
        elref: "breakdownSection",
        entries: JSON.stringify(segmentEntries),
        'event-names': JSON.stringify(this.helpers.getEntryValues("entries")),
        'exclude-dimension-properties': this.attr("exclude-dimension-properties"),
        'include-custom-properties': this.attr("include-custom-properties"),
        'inline-cohorts-override': this.attr("inline-cohorts-override"),
        'other-group-by-options': this.attr("other-group-by-options"),
        'project-id': this.attr("project-id"),
        'row-draggable': true,
        'row-label-type': this.helpers.getNoneRowLabelType(),
        'row-limit': this.attr("group-by-limitation"),
        'row-types': JSON.stringify([this.helpers.getSegmentEntryRowType()]),
        'show-special-date-property': this.attr("show-special-date-property"),
        'show-special-dataset-property': this.attr("show-special-dataset-property"),
        'support-b2b-cohorts': this.attr("support-b2b-cohorts"),
        'undraggable-rows': this.attr("group-by-undraggable-rows"),
        'user-id': this.attr("user-id"),
        'workspace-id': this.attr("workspace-id"),
        disabled: this.attr("disable-breakdown"),
        'section-title': "Breakdown"
      })),
      on: {
        savedCustomEvent: this.helpers.onSavedCustomEvent,
        track: this.helpers.captureTracking,
        update: this.helpers.updateSegmentEntrySection
      }
    }, hasNewUI && jsx("div", {
      attrs: {
        slot: "header-items"
      }
    }, jsx(AttributionControl, {
      disabled: this.attr("disable-breakdown")
    })));
  };

  const GlobalFilters = _ref2 => {
    let {
      filterEntries
    } = _ref2;
    const menuAttrs = this.helpers.getPropertyMenuAttributes("filter");
    return jsx(QueryEntrySectionEl, {
      key: "".concat(this.attr("selected-query-type"), "-filters"),
      attrs: this.helpers.serializeAttributes(Object.assign(menuAttrs, {
        elref: "filterSection",
        'data-group-id': this.attr("data-group-id"),
        'data-groups-enabled': menuAttrs["data-groups-enabled"] && !this.attr("filter-hide-data-groups"),
        'enable-enhanced-list-filters': this.attr("enable-enhanced-list-filters"),
        'event-names': JSON.stringify(this.helpers.getEntryValues("entries")),
        entries: JSON.stringify(filterEntries),
        'exclude-dimension-properties': this.attr("exclude-dimension-properties"),
        'inline-cohorts-override': this.attr("inline-cohorts-override"),
        'hide-events': menuAttrs["hide-events"],
        'other-filter-options': this.attr("other-filter-options"),
        'project-has-people': menuAttrs["project-has-people"] && !this.attr("filter-hide-users"),
        'project-id': this.attr("project-id"),
        'row-label-type': this.helpers.getNoneRowLabelType(),
        'row-draggable': true,
        'row-types': JSON.stringify([this.helpers.getFilterEntryRowType()]),
        'show-special-dataset-property': this.attr("show-special-dataset-property"),
        'show-special-date-property': this.attr("show-special-date-property"),
        'support-b2b-cohorts': this.attr("support-b2b-cohorts"),
        'user-id': this.attr("user-id"),
        'workspace-id': this.attr("workspace-id"),
        'section-title': "Filter"
      })),
      on: {
        savedCustomEvent: this.helpers.onSavedCustomEvent,
        track: this.helpers.captureTracking,
        update: this.helpers.updateFilterEntrySection
      }
    }, hasNewUI && jsx("div", {
      attrs: {
        slot: "header-items"
      }
    }, this.helpers.showFilterOrOperatorInfo() && jsx(FilterOrOperatorInfo, null), this.attr("enable-filter-attribution") && jsx(AttributionControl, null)));
  };

  const ExclusionSteps = () => this.attr("show-exclusion-steps") && jsx(SectionEl, {
    attrs: {
      'header-title': !hasNewUI && "Exclusion Steps",
      'is-closed': !hasNewUI && this.helpers.getEntries("exclusion-steps").length === 0,
      'non-collapsible': hasNewUI
    },
    on: {
      track: this.helpers.captureTracking
    }
  }, jsx("div", {
    sel: '.mp-query-body'
  }, jsx(QueryEntrySectionEl, {
    attrs: {
      'add-entry-label': this.attr("add-exclusion-event-label"),
      'add-entry-button-tooltip-text': this.attr("exclusion-step-add-entry-tooltip-text"),
      'data-group-id': this.attr("data-group-id"),
      'data-groups-enabled': this.attr("data-groups-enabled"),
      'disable-first-time-event-filters': this.attr("disable-first-time-event-filters"),
      entries: this.helpers.getEntries("exclusion-steps"),
      'row-draggable': true,
      'entry-type': EntryType.ExclusionEvent,
      'exclude-autotrack-events': this.attr("exclude-autotrack-events"),
      'include-custom-properties': this.attr("include-custom-properties"),
      'inline-cohorts-override': this.attr("inline-cohorts-override"),
      'is-blocked-people': Boolean(this.attr("project-has-people")),
      'num-exclusion-steps': this.helpers.getNumExclusionSteps(),
      'project-has-people': this.attr("project-has-people"),
      'project-has-cohorts': this.attr("project-has-cohorts"),
      'project-id': this.attr("project-id"),
      'track-name': this.attr("track-name"),
      'workspace-id': this.attr("workspace-id"),
      'project-as-of-date': this.attr("project-as-of-date"),
      'section-title': "Exclusion Steps",
      'row-label-type': hasNewUI ? RowLabelType.None : this.attr("entry-row-label-type")
    },
    on: {
      savedCustomEvent: this.helpers.onSavedCustomEvent,
      track: this.helpers.captureTracking,
      update: ev => this.helpers.updateEntrySection(ev, "exclusion-steps")
    }
  })));

  const EntriesSection = () => jsx(QueryEntrySectionEl, {
    key: "".concat(this.attr("selected-query-type"), "-entries"),
    // @todo [pablo] enable this for funnel steps & retention only once more reports are integrated
    class: {
      'contained-section': hasNewUI
    },
    attrs: this.helpers.serializeAttributes(Object.assign({
      elref: "entrySection",
      autofocus: !entries.length || !entries.some(_ref3 => {
        let {
          event
        } = _ref3;
        return "value" in event && event.value;
      }),
      'add-entry-label': this.attr("add-entry-label"),
      'add-entry-button-label': this.attr("add-entry-button-label"),
      'add-entry-button-tooltip-text': this.attr("add-entry-button-tooltip-text"),
      'allow-cohort-filter': this.attr("allow-cohort-filter"),
      'behavior-mode': this.attr("behavior-mode"),
      'can-customize-numeric-buckets': this.attr("can-customize-numeric-buckets"),
      'can-rename-step': this.attr("show-entry-rename"),
      'enable-enhanced-list-filters': this.attr("enable-enhanced-list-filters"),
      'group-event-behavior': this.attr("can-group-events") ? "nest" : "none",
      'data-group-id': this.attr("data-group-id"),
      'data-groups-enabled': this.attr("data-groups-enabled"),
      'date-range': this.attr("date-range"),
      'disable-add-event-filters': this.attr("disable-add-event-filters"),
      'disable-add-entry': this.attr("disable-add-entry"),
      'disable-add-grouped-event-reason': this.attr("disable-add-grouped-event-reason"),
      'disable-clone-entry': this.attr("disable-clone-entry"),
      'disable-delete-entry': this.attr("disable-delete-entry"),
      'disable-first-time-event-filters': this.attr("disable-first-time-event-filters"),
      entries: JSON.stringify(entries),
      'exclude-autotrack-events': this.attr("exclude-autotrack-events"),
      'exclude-custom-events': this.attr("exclude-custom-events"),
      'exclude-dimension-properties': this.attr("exclude-dimension-properties"),
      'include-custom-properties': this.attr("include-custom-properties"),
      'inline-cohorts-override': this.attr("inline-cohorts-override"),
      'is-blocked-people': !this.attr("project-has-people"),
      'max-data-history-days': this.attr("max-data-history-days"),
      'metrics-mode': this.attr("metrics-mode"),
      'project-has-people': this.attr("project-has-people"),
      'hide-edit-controls': this.attr("hide-edit-controls"),
      'hide-events': true,
      'project-has-cohorts': this.attr("project-has-cohorts"),
      'project-id': this.attr("project-id"),
      readonly: this.attr("readonly-entries"),
      'row-draggable': this.attr("entries-draggable"),
      'row-index-toggleable': this.attr("entry-index-toggleable"),
      'row-index-toggle-label': this.attr("entry-index-toggle-label"),
      'row-label-type': this.attr("entry-row-label-type"),
      'row-limit': this.attr("row-limit"),
      'row-types': this.attr("row-types"),
      'show-any-event': this.attr("show-any-event"),
      'show-all-events': this.attr("show-all-events"),
      'show-sessions': this.attr("show-sessions"),
      'show-special-dataset-property': this.attr("show-special-dataset-property"),
      'support-b2b-cohorts': this.attr("support-b2b-cohorts"),
      'time-display-context': this.attr("time-display-context"),
      'track-name': this.attr("track-name"),
      'track-properties': this.attr("track-properties"),
      'user-id': this.attr("user-id"),
      'workspace-id': this.attr("workspace-id"),
      'project-as-of-date': this.attr("project-as-of-date")
    }, this.helpers.getMetricsModeAttributes())),
    on: {
      savedCustomEvent: this.helpers.onSavedCustomEvent,
      track: this.helpers.captureTracking,
      update: ev => this.helpers.updateEntrySection(ev, "entries")
    }
  }, jsx("slot", {
    attrs: {
      name: "footer-items",
      slot: "footer-items"
    }
  }));

  return jsx("div", {
    sel: '.mp-query-builder',
    class: {
      'for-new-ui': hasNewUI
    }
  }, this.attr("data-groups-enabled") && !this.attr("hide-data-groups-selector") && !this.attr("report-header-only") && jsx(DataGroupSelectorEl, {
    attrs: {
      'project-id': this.attr("project-id"),
      'selected-data-group-id': this.attr("data-group-id"),
      'select-warnings': JSON.stringify(this.helpers.getDataGroupSelectWarnings()),
      theme: "builder",
      'workspace-id': this.attr("workspace-id")
    },
    on: {
      selectDataGroup: this.helpers.onDataGroupSelected
    }
  }), this.attr("report-header-attrs") && jsx("div", {
    sel: '.mp-query-report-header',
    class: {
      'boards-mode': this.helpers.isBoardsUiEnabled(),
      'is-sticky': this.helpers.useStickyHeader()
    },
    hook: {
      insert: this.helpers.handleReportHeaderInsert
    }
  }, jsx(QueryReportHeaderEl, {
    attrs: this.helpers.serializeAttributes(this.attr("report-header-attrs")),
    on: {
      actionclick: this.helpers.actionClick,
      bookmarkselectoropen: this.helpers.propagateEvent,
      bookmarkselect: this.helpers.propagateEvent,
      reset: this.helpers.propagateEvent,
      save: this.helpers.propagateEvent,
      update: this.helpers.propagateEvent
    }
  }, this.helpers.getHeaderActionItemsSlots().map(item => jsx("slot", {
    attrs: {
      name: item.slot,
      slot: item.slot
    }
  })))), !this.attr("report-header-only") && jsx("div", {
    sel: '.mp-query-builder-panel.for-body',
    class: {
      'section-content-only': !this.attr("header-title") && this.attr("main-entry-non-collapsible")
    }
  }, this.attr("query-type-options") && jsx("div", {
    sel: '.mp-query-type-selection'
  }, jsx(ToggleEl, {
    attrs: {
      elref: "queryTypeSelector",
      theme: "tab-bar",
      selected: this.attr("selected-query-type"),
      options: JSON.stringify(this.attr("query-type-options"))
    },
    on: {
      select: this.helpers.onQueryTypeSelected
    }
  })), jsx(SectionEl, {
    attrs: {
      'header-title': this.attr("header-title"),
      'header-title-lowercase': this.attr("header-title-lowercase"),
      'non-collapsible': hasNewUI || this.attr("main-entry-non-collapsible"),
      'is-container': hasNewUI
    },
    on: {
      track: this.helpers.captureTracking
    }
  }, jsx("div", {
    sel: '.mp-query-body'
  }, jsx(EntriesSection, null))), jsx(ExclusionSteps, null), jsx("div", {
    sel: '.mp-query-body-sections'
  }, jsx("slot", {
    attrs: {
      name: "body-section"
    },
    on: {
      track: this.helpers.captureTracking
    }
  })), !this.attr("hide-filter") && jsx(SectionEl, {
    attrs: {
      elref: "filterSectionDropdown",
      'header-title': !hasNewUI && "Filters",
      'is-closed': !this.helpers.getFilterEntries().length && !hasNewUI,
      'non-collapsible': hasNewUI
    },
    on: {
      track: this.helpers.captureTracking
    }
  }, jsx("div", {
    sel: '.mp-query-body'
  }, this.helpers.showFilterOrOperatorInfo() && jsx(FilterOrOperatorInfo, null), jsx(GlobalFilters, {
    filterEntries: this.helpers.getFilterEntries()
  }), this.attr("enable-filter-attribution") && !hasNewUI && jsx(AttributionControl, null))), !this.attr("hide-group-by") && jsx(SectionEl, {
    attrs: {
      elref: "breakdownSectionDropdown",
      'header-title': !hasNewUI && "Breakdowns",
      'is-closed': !globalSegments.length && !hasNewUI,
      'non-collapsible': hasNewUI
    },
    on: {
      track: this.helpers.captureTracking
    }
  }, jsx("div", {
    sel: '.mp-query-body'
  }, jsx(GlobalBreakdowns, {
    segmentEntries: globalSegments
  }), !hasNewUI && jsx(AttributionControl, {
    disabled: this.attr("disable-breakdown")
  })))));
}